import { Phone, Mail } from "lucide-react";
import { COMPANY, FAQS } from "@/lib/site";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

export default function Faqs() {
    return (
        <>
            <SEO
                title="Heat Pump FAQs | Virtex Renewables"
                description="Answers to common questions about air source heat pumps, radiators, hot water, winter performance and the Boiler Upgrade Scheme. Virtex Renewables — England & Wales."
            />

            <section className="relative overflow-hidden bg-slate-50 py-16 md:py-24" data-testid="faqs-hero">
                <div className="pointer-events-none absolute -right-40 -top-40 h-[24rem] w-[24rem] rounded-full bg-emerald-100/70 blur-3xl" />
                <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
                    <Reveal>
                        <span className="inline-block rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                            FAQs
                        </span>
                        <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
                            Heat Pump Questions, Answered
                        </h1>
                        <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
                            Straightforward answers to the questions homeowners and landlords ask us most about air
                            source heat pumps and the Boiler Upgrade Scheme.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                            {FAQS.map((faq, i) => (
                                <AccordionItem key={faq.q} value={`item-${i}`} className="border-slate-200" data-testid={`faq-item-${i}`}>
                                    <AccordionTrigger
                                        className="py-5 text-left font-display text-base font-semibold text-slate-900 hover:text-emerald-700 hover:no-underline sm:text-lg"
                                        data-testid={`faq-trigger-${i}`}
                                    >
                                        {faq.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-sm leading-relaxed text-slate-600 sm:text-base" data-testid={`faq-content-${i}`}>
                                        {faq.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>

                    <Reveal className="mt-14">
                        <div className="rounded-3xl bg-slate-950 p-8 text-center md:p-12" data-testid="faq-support-banner">
                            <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">Still Have a Question?</h2>
                            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
                                Every property is different. If you'd like an answer specific to your home, our team
                                is happy to help.
                            </p>
                            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                                <a
                                    href={COMPANY.phoneHref}
                                    data-testid="faq-call-button"
                                    className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-emerald-500 sm:w-auto"
                                >
                                    <Phone className="h-4 w-4" /> Call {COMPANY.phoneDisplay}
                                </a>
                                <a
                                    href={COMPANY.emailHref}
                                    data-testid="faq-email-button"
                                    className="flex w-full items-center justify-center gap-2 rounded-full border border-slate-700 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:border-emerald-500 hover:text-emerald-400 sm:w-auto"
                                >
                                    <Mail className="h-4 w-4" /> {COMPANY.email}
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
